import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Button, Col, Form, Row, Table, Spinner,
  Pagination, Modal,
} from 'react-bootstrap';
import axios from '../../api/axios';
import { toast } from 'react-toastify';
import DatePicker from 'react-datepicker';
import {
  ArrowLeft, FileEarmarkSpreadsheet, CloudDownload, X,
  Clock, ArrowClockwise, CheckCircle, ExclamationTriangle,
  HourglassSplit, BoxArrowDown, XCircle,
} from 'react-bootstrap-icons';
import Header from '../../layouts/Header';
import { useNavigate } from 'react-router-dom';
import OutletSelect from '../../components/OutletSelect';

/* ─── helpers ─── */
const safeArray = (val) => (Array.isArray(val) ? val : []);

const fmtCurrency = (n) =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency', currency: 'INR',
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  }).format(n || 0);

const fmtDate = (iso) =>
  new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' });

const toDateStr = (d) => {
  if (!d) return '';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const isLongRange = (start, end) => {
  if (!start || !end) return false;
  const maxEnd = new Date(start);
  maxEnd.setMonth(maxEnd.getMonth() + 3);
  return end > maxEnd;
};

const REPORT_TYPE = 'JOURNEY_COUPON_REVENUE_REPORT';

/* ─── Download-manager CSS (same pattern as CouponsReport) ─── */
const DM_CSS = `
  :root {
    --jr-bg:#f0f2f5; --jr-surface:#fff; --jr-border:#e2e6ea; --jr-text:#1a202c;
    --jr-muted:#41505F; --jr-accent:#2563eb; --jr-accent-light:#eff6ff; --jr-accent-dark:#1d4ed8;
    --jr-success:#059669; --jr-success-light:#ecfdf5; --jr-warning:#d97706; --jr-warning-light:#fffbeb;
    --jr-danger:#dc2626; --jr-danger-light:#fef2f2;
    --jr-radius:10px; --jr-radius-xs:6px;
  }
  .jr-dm-fab{height:38px;border-radius:var(--jr-radius-xs);padding:0 16px;background:#fff;border:1.5px solid var(--jr-border);display:inline-flex;align-items:center;justify-content:center;cursor:pointer;transition:all .18s;color:var(--jr-muted);font-size:13px;font-weight:600;gap:7px;white-space:nowrap;box-shadow:0 1px 3px rgba(0,0,0,.08);}
  .jr-dm-fab:hover,.jr-dm-fab.has-active{border-color:var(--jr-accent);color:var(--jr-accent);background:var(--jr-accent-light);}
  .jr-dm-badge{background:#dc2626;color:#fff;width:18px;height:18px;border-radius:50%;margin-left:2px;font-size:10px;font-weight:700;display:inline-flex;align-items:center;justify-content:center;}

  .jr-dm-panel{position:fixed;top:72px;right:24px;z-index:1049;width:580px;max-height:700px;background:var(--jr-surface);border-radius:14px;border:1.5px solid var(--jr-border);box-shadow:0 20px 60px rgba(0,0,0,.15),0 4px 16px rgba(0,0,0,.08);display:flex;flex-direction:column;overflow:hidden;animation:jr-dm-in .2s ease;}
  @keyframes jr-dm-in{from{opacity:0;transform:translateY(-8px) scale(.97);}to{opacity:1;transform:translateY(0) scale(1);}}

  .jr-dm-header{display:flex;align-items:flex-start;justify-content:space-between;padding:20px 22px 18px;background:linear-gradient(135deg,#0f172a 0%,#1e3a8a 60%,#1d4ed8 100%);color:#fff;flex-shrink:0;}
  .jr-dm-header-left{flex:1;}
  .jr-dm-header-title{display:flex;align-items:center;gap:9px;font-size:15px;font-weight:700;letter-spacing:-.2px;}
  .jr-dm-header-sub{font-size:11.5px;opacity:.6;margin-top:3px;}
  .jr-dm-stats{display:flex;gap:10px;margin-top:14px;}
  .jr-dm-stat{background:rgba(255,255,255,.1);border-radius:8px;padding:9px 16px;text-align:center;border:1px solid rgba(255,255,255,.12);min-width:70px;}
  .jr-dm-stat-val{font-size:20px;font-weight:700;color:#fff;line-height:1;}
  .jr-dm-stat-lbl{font-size:10px;opacity:.65;margin-top:4px;text-transform:uppercase;letter-spacing:.6px;}
  .jr-dm-header-actions{display:flex;align-items:center;gap:6px;flex-shrink:0;padding-top:2px;}
  .jr-dm-icon-btn{width:30px;height:30px;border-radius:7px;border:none;background:rgba(255,255,255,.12);color:#fff;cursor:pointer;display:flex;align-items:center;justify-content:center;transition:background .15s;}
  .jr-dm-icon-btn:hover{background:rgba(255,255,255,.25);}

  .jr-dm-body{overflow-y:auto;flex:1;padding:14px 16px;display:flex;flex-direction:column;gap:10px;background:#f8fafc;}
  .jr-dm-body::-webkit-scrollbar{width:5px;}
  .jr-dm-body::-webkit-scrollbar-thumb{background:#d1d5db;border-radius:4px;}

  .jr-dm-job{display:flex;align-items:center;gap:12px;padding:14px 16px;border-radius:10px;border:1px solid var(--jr-border);background:#fff;transition:box-shadow .15s,border-color .15s;}
  .jr-dm-job:hover{box-shadow:0 4px 12px rgba(0,0,0,.09);border-color:#cbd5e1;}
  .jr-dm-job-icon{width:40px;height:40px;border-radius:10px;display:flex;align-items:center;justify-content:center;flex-shrink:0;font-size:17px;}
  .jr-dm-job-icon.pending{background:#fef3c7;color:#d97706;}
  .jr-dm-job-icon.processing{background:var(--jr-accent-light);color:var(--jr-accent);}
  .jr-dm-job-icon.completed{background:var(--jr-success-light);color:var(--jr-success);}
  .jr-dm-job-icon.failed{background:var(--jr-danger-light);color:var(--jr-danger);}
  .jr-dm-job-info{flex:1;min-width:0;}
  .jr-dm-job-name{font-size:13px;font-weight:600;color:var(--jr-text);white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
  .jr-dm-job-meta{font-size:11.5px;color:#64748b;margin-top:3px;display:flex;align-items:center;gap:6px;flex-wrap:wrap;}

  .jr-pb-wrap{height:4px;background:#e2e8f0;border-radius:3px;margin-top:7px;overflow:hidden;}
  .jr-pb{height:100%;border-radius:3px;transition:width .5s ease;}
  .jr-pb.pending{background:#fcd34d;width:8%;}
  .jr-pb.processing{background:var(--jr-accent);width:60%;animation:jr-pb-shimmer 1.5s ease-in-out infinite;}
  .jr-pb.completed{background:var(--jr-success);width:100%;}
  .jr-pb.failed{background:var(--jr-danger);width:100%;}
  @keyframes jr-pb-shimmer{0%,100%{opacity:1;}50%{opacity:.55;}}

  .jr-status-pill{font-size:10.5px;font-weight:700;padding:3px 10px;border-radius:20px;white-space:nowrap;flex-shrink:0;}
  .jr-status-pill.pending{background:#fef3c7;color:#d97706;}
  .jr-status-pill.processing{background:var(--jr-accent-light);color:var(--jr-accent);}
  .jr-status-pill.completed{background:var(--jr-success-light);color:var(--jr-success);}
  .jr-status-pill.failed{background:var(--jr-danger-light);color:var(--jr-danger);}

  .jr-dl-btn{height:32px;padding:0 14px;border-radius:var(--jr-radius-xs);border:1.5px solid #a7f3d0;background:var(--jr-success-light);color:var(--jr-success);font-size:12px;font-weight:600;cursor:pointer;display:inline-flex;align-items:center;gap:5px;transition:all .15s;white-space:nowrap;flex-shrink:0;}
  .jr-dl-btn:hover{background:var(--jr-success);color:#fff;border-color:var(--jr-success);}

  .jr-del-btn{width:30px;height:30px;border-radius:var(--jr-radius-xs);border:1.5px solid #fecaca;background:var(--jr-danger-light);color:var(--jr-danger);cursor:pointer;display:inline-flex;align-items:center;justify-content:center;transition:all .15s;flex-shrink:0;}
  .jr-del-btn:hover:not(:disabled){background:var(--jr-danger);color:#fff;border-color:var(--jr-danger);}
  .jr-del-btn:disabled{opacity:.5;cursor:not-allowed;}

  .jr-regen-btn{width:30px;height:30px;border-radius:var(--jr-radius-xs);border:1.5px solid #bfdbfe;background:var(--jr-accent-light);color:var(--jr-accent);cursor:pointer;display:inline-flex;align-items:center;justify-content:center;transition:all .15s;flex-shrink:0;}
  .jr-regen-btn:hover:not(:disabled){background:var(--jr-accent);color:#fff;border-color:var(--jr-accent);}
  .jr-regen-btn:disabled{opacity:.5;cursor:not-allowed;}

  .jr-dm-footer{padding:12px 18px;border-top:1px solid var(--jr-border);flex-shrink:0;display:flex;align-items:center;justify-content:space-between;background:#fff;}
  .jr-dm-footer-info{font-size:12px;color:var(--jr-muted);display:flex;align-items:center;gap:5px;}
  .jr-dm-empty{display:flex;flex-direction:column;align-items:center;justify-content:center;padding:48px 20px;color:var(--jr-muted);text-align:center;gap:10px;}

  .jr-spin{animation:jr-spin .9s linear infinite;display:inline-block;}
  @keyframes jr-spin{from{transform:rotate(0deg);}to{transform:rotate(360deg);}}
  .jr-pulse{animation:jr-pulse 1.5s ease-in-out infinite;}
  @keyframes jr-pulse{0%,100%{opacity:1;}50%{opacity:.5;}}

  @media(max-width:768px){.jr-dm-panel{width:calc(100vw - 32px);left:16px;right:16px;top:72px;}}
`;

const JourneyRevenueReport = () => {
  const currentSkin = localStorage.getItem('skin-mode') ? 'dark' : '';
  const [skin, setSkin] = useState(currentSkin);
  const navigate = useNavigate();
  const accessToken = localStorage.getItem('authToken');

  /* ── filters ── */
  const [selectedOutlets, setSelectedOutlets] = useState([]);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [error, setError] = useState('');

  /* ── data / ui state ── */
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [btnLoading, setBtnLoading] = useState(false);
  const [downloadBtnLoading, setDownloadBtnLoading] = useState(false);
  const [clicked, setClicked] = useState(false);
  const [isScheduledMode, setIsScheduledMode] = useState(false);

  /* ── pagination ── */
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(25);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);

  /* ── sort ── */
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  /* ── cumulative-choice modal ── */
  const [showCumulativeModal, setShowCumulativeModal] = useState(false);
  const [pendingDownloadParams, setPendingDownloadParams] = useState(null);

  /* ── Download Manager ── */
  const [dmStartDate, setDmStartDate] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  });
  const [dmEndDate, setDmEndDate] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  });
  const [dmOpen, setDmOpen] = useState(false);
  const [downloadJobs, setDownloadJobs] = useState([]);
  const [pollingActive, setPollingActive] = useState(false);
  const [jobsLoading, setJobsLoading] = useState(false);
  const pollingRef = useRef(null);
  const dmPanelRef = useRef(null);
  const [deletingJobId, setDeletingJobId] = useState(null);
  const [regeneratingJobId, setRegeneratingJobId] = useState(null);

  /* ════════════════════════════════ INIT ════════════════════════════════ */
  useEffect(() => {
    const now = new Date();
    const first = new Date(now.getFullYear(), now.getMonth(), 1);
     const today = new Date();
    setStartDate(new Date(first.setHours(23, 59, 59, 999)));
    setEndDate(new Date(today.setHours(23, 59, 59, 999)));
    fetchDownloadStatus(true);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dmPanelRef.current && !dmPanelRef.current.contains(e.target)) {
        const fab = document.getElementById('jr-dm-fab');
        if (fab && !fab.contains(e.target)) setDmOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => () => stopPolling(), []);

  /* ─── re-fetch when recordsPerPage changes after first load ─── */
  useEffect(() => {
    if (clicked && !isScheduledMode) {
      fetchReportData(1, recordsPerPage);
    }
  }, [recordsPerPage]);

  /* ════════════════════════════════ DOWNLOAD MANAGER ════════════════════════════════ */
  const fmtViewDate = (d) =>
    new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  const fmtViewTime = (d) =>
    new Date(d).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });

  const fetchDownloadStatus = useCallback(
    async (silent = false, overrideStart, overrideEnd) => {
      try {
        if (!silent) setJobsLoading(true);
        const headers = { Authorization: `Bearer ${accessToken}` };
        const sDate = overrideStart !== undefined ? overrideStart : dmStartDate;
        const eDate = overrideEnd !== undefined ? overrideEnd : dmEndDate;
        const res = await axios.get(`report/get-report-status/${REPORT_TYPE}`, {
          headers,
          params: { startDate: toDateStr(sDate), endDate: toDateStr(eDate) },
        });
        const jobs = res?.data?.data?.reportRequests || res?.data?.reportRequests || [];
        const jobArr = Array.isArray(jobs) ? jobs : [];
        const sorted = [...jobArr].sort((a, b) => {
          const termA = a.status === 'COMPLETED' || a.status === 'FAILED';
          const termB = b.status === 'COMPLETED' || b.status === 'FAILED';
          if (termA !== termB) return termA ? 1 : -1;
          return new Date(b.createdAt) - new Date(a.createdAt);
        });
        setDownloadJobs(sorted);
        const allDone = jobArr.every((j) => j.status === 'COMPLETED' || j.status === 'FAILED');
        if (allDone && jobArr.length > 0) stopPolling();
      } catch (e) {
        console.error('Poll error:', e);
      } finally {
        if (!silent) setJobsLoading(false);
      }
    },
    [accessToken, dmStartDate, dmEndDate]
  );

  const startPolling = useCallback(() => {
    setPollingActive(true);
    fetchDownloadStatus(true);
    if (pollingRef.current) clearInterval(pollingRef.current);
    pollingRef.current = setInterval(() => fetchDownloadStatus(true), 5000);
  }, [fetchDownloadStatus]);

  const stopPolling = useCallback(() => {
    setPollingActive(false);
    if (pollingRef.current) { clearInterval(pollingRef.current); pollingRef.current = null; }
  }, []);

  const handleDmDateChange = (type, date) => {
    if (type === 'start') {
      setDmStartDate(date);
      if (date && dmEndDate) {
        const diff = (dmEndDate.getTime() - date.getTime()) / (1000 * 60 * 60 * 24);
        if (diff > 30) {
          const clamped = new Date(date);
          clamped.setDate(clamped.getDate() + 30);
          setDmEndDate(clamped);
          fetchDownloadStatus(false, date, clamped);
          return;
        }
      }
      fetchDownloadStatus(false, date, dmEndDate);
    } else {
      setDmEndDate(date);
      fetchDownloadStatus(false, dmStartDate, date);
    }
  };

  /* ─── job status helpers ─── */
  const getJobCls = (s) => {
    const v = s?.toLowerCase();
    if (v === 'completed') return 'completed';
    if (v === 'failed') return 'failed';
    if (v === 'no_data') return 'no_data';
    if (v === 'processing' || v === 'in-progress') return 'processing';
    return 'pending';
  };
  const getJobIcon = (s) => {
    const c = getJobCls(s);
    if (c === 'completed') return <CheckCircle size={15} />;
    if (c === 'failed') return <ExclamationTriangle size={15} />;
    if (c === 'processing') return <span className="jr-spin"><ArrowClockwise size={15} /></span>;
    return <HourglassSplit size={15} />;
  };
  const getJobLabel = (s) => ({
    completed: 'Completed',
    failed: 'Failed',
    no_data: 'No data found',
    processing: 'Processing…',
    pending: 'Queued',
  }[getJobCls(s)] || 'Queued');

  const pendingCount = downloadJobs.filter(
    (j) => getJobCls(j.status) === 'pending' || getJobCls(j.status) === 'processing'
  ).length;
  const completedCount = downloadJobs.filter((j) => getJobCls(j.status) === 'completed').length;

  const downloadFile = (job) => {
    const url = job.storagePath;
    if (!url) { toast.error('File URL not available.'); return; }
    const link = document.createElement('a');
    link.href = url;
    link.download = `Journey_Coupon_Revenue_${job.queryParams?.startDate}_${job.queryParams?.endDate}.xlsx`;
    document.body.appendChild(link); link.click(); document.body.removeChild(link);
  };

  const handleDeleteReportRequest = async (jobId) => {
    try {
      setDeletingJobId(jobId);
      await axios.delete('report/delete-report-request', {
        headers: { Authorization: `Bearer ${accessToken}` },
        data: { requestId: jobId },
      });
      toast.success('Request cancelled.', { position: 'top-right', autoClose: 2000, theme: 'light' });
      fetchDownloadStatus(true);
    } catch {
      toast.error('Failed to cancel request.', { position: 'top-right', autoClose: 3000, theme: 'light' });
    } finally {
      setDeletingJobId(null);
    }
  };

  const handleRegenerateReport = async (jobId) => {
    try {
      setRegeneratingJobId(jobId);
      await axios.delete('report/delete-report-storage-path', {
        headers: { Authorization: `Bearer ${accessToken}` },
        data: { requestId: jobId },
      });
      toast.success('Regeneration triggered!', { position: 'top-right', autoClose: 2500, theme: 'light' });
      startPolling();
      fetchDownloadStatus(true);
    } catch {
      toast.error('Failed to trigger regeneration.', { position: 'top-right', autoClose: 3000, theme: 'light' });
    } finally {
      setRegeneratingJobId(null);
    }
  };

  /* ════════════════════════════════ API CALLS ════════════════════════════════ */
  const buildParams = (sd, ed, extras = {}) => {
    const params = new URLSearchParams();
    if (sd) params.append('startDate', toDateStr(sd));
    if (ed) params.append('endDate', toDateStr(ed));
    Object.entries(extras).forEach(([k, v]) => { if (v !== undefined && v !== null) params.append(k, String(v)); });
    return params.toString();
  };

  /**
   * Fetch inline report data (single outlet or ≤90 days, single/no outlet).
   * Always sends isCumulative=true for multiple outlets so the API merges data.
   */
  const fetchReportData = async (page = 1, limit = recordsPerPage) => {
    if (!startDate || !endDate) {
      toast.error('Please select start and end date.');
      return;
    }
    try {
      setLoading(true);
      const outletIds = selectedOutlets?.map((o) => o.value);
      const isMulti = outletIds.length > 1 || outletIds.length === 0;
      const qs = buildParams(startDate, endDate, {
        page,
        limit,
        isDownload: false,
        isCumilative: isMulti ? true : false,
      });
      const res = await axios.post(
        `/auto-coupon-allocation/getAutoCouponUsageSummary?${qs}`,
        { outletIds },
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      if (res?.data?.status === true) {
        setReportData(safeArray(res.data.data));
        setTotalPages(res?.data?.pagination?.totalPages || 1);
        setTotalRecords(res?.data?.pagination?.totalRecords || 0);
        setCurrentPage(page);
      } else {
        setReportData([]);
        toast.info('No data found for the selected criteria.');
      }
    } catch (e) {
      handleApiError(e);
      setReportData([]);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Trigger background download job.
   * isCumulative param comes from caller (modal choice or auto).
   */
  const triggerScheduledDownload = async (isCumilative) => {
    try {
      setDownloadBtnLoading(true);
      const outletIds = selectedOutlets.map((o) => o.value);
      const qs = buildParams(startDate, endDate, { isDownload: true, isCumilative });
      await axios.post(
        `/auto-coupon-allocation/getAutoCouponUsageSummary?${qs}`,
        { outletIds },
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      toast.success('Download job queued! Check the Downloads panel.', { position: 'top-right', autoClose: 2500, theme: 'light' });
      setDmOpen(true);
      startPolling();
    } catch (e) {
      const s = e?.response?.status;
      const msg = s === 406
        ? e.response?.data?.error?.message || e.response?.data?.error
        : 'Failed to queue download job.';
      toast.error(msg, { position: 'top-right', autoClose: 3000, theme: 'light' });
    } finally {
      setDownloadBtnLoading(false);
    }
  };

  const handleApiError = (e) => {
    let msg = 'An error occurred';
    if (e.response?.status === 406) msg = e.response?.data?.error?.message || e.response?.data?.error;
    else if (e.response?.status === 412) msg = e.response?.data?.data?.message;
    else if (e.response?.status === 500) msg = e.response?.data?.error;
    toast.error(msg, { position: 'top-right', autoClose: 3000, theme: 'light' });
  };

  /* ─── Submit handler ─── */
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!startDate || !endDate) { toast.error('Please select start and end date.'); return; }

    setClicked(true);
    setBtnLoading(true);

    const isMulti = selectedOutlets?.length > 1 || selectedOutlets?.length === 0;
    const longRange = isLongRange(startDate, endDate);
    const scheduleNeeded = longRange; // single/no outlet + >90 days always schedules

    if (scheduleNeeded) {
      // Multiple outlets + long range → ask cumulative or outlet-wise
      if (isMulti) {
        setPendingDownloadParams({ isCumilative: null });
        setShowCumulativeModal(true);
        setBtnLoading(false);
        setIsScheduledMode(true);
        setReportData([]);
        return;
      }
      // Single / no outlet + long range → schedule directly (no cumulative choice needed)
      setIsScheduledMode(true);
      setReportData([]);
      await triggerScheduledDownload(false);
    } else {
      // Inline mode: single or multiple outlets, ≤90 days
      setIsScheduledMode(false);
      await fetchReportData(1, recordsPerPage);
    }

    setBtnLoading(false);
  };

  /* ─── Inline Excel download (single outlet, short range) ─── */
  const handleDownloadExcel = async () => {
    try {
      setDownloadBtnLoading(true);
      const outletIds = selectedOutlets.map((o) => o.value);
      const qs = buildParams(startDate, endDate, { isDownload: true, isCumilative: true });
      const res = await axios.post(
        `/auto-coupon-allocation/getAutoCouponUsageSummary?${qs}`,
        { outletIds },
        { headers: { Authorization: `Bearer ${accessToken}` }, responseType: 'blob' }
      );
      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      const name = selectedOutlets.length > 0 ? selectedOutlets.map((o) => o.label).join(', ') : 'All Outlets';
      link.download = `${name} Journey Coupon Revenue (${toDateStr(startDate)} to ${toDateStr(endDate)}).xlsx`;
      document.body.appendChild(link); link.click();
      document.body.removeChild(link); window.URL.revokeObjectURL(link.href);
      toast.success('Excel downloaded!', { position: 'top-right', autoClose: 3000, theme: 'light' });
    } catch (e) {
      handleApiError(e);
    } finally {
      setDownloadBtnLoading(false);
    }
  };

  /* ─── Pagination ─── */
  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    fetchReportData(page, recordsPerPage);
  };

  const renderPaginationItems = () => {
    if (totalPages <= 1) return null;
    if (totalPages > 15) {
      return (
        <>
          {currentPage > 3 && <Pagination.Item onClick={() => handlePageChange(1)}>1</Pagination.Item>}
          {currentPage > 4 && <Pagination.Ellipsis />}
          {Array.from({ length: 5 }, (_, i) => {
            const p = currentPage > 3 ? currentPage - 2 + i : i + 1;
            if (p <= totalPages)
              return <Pagination.Item key={p} active={p === currentPage} onClick={() => handlePageChange(p)}>{p}</Pagination.Item>;
            return null;
          })}
          {currentPage < totalPages - 3 && <Pagination.Ellipsis />}
          {currentPage < totalPages - 2 && <Pagination.Item onClick={() => handlePageChange(totalPages)}>{totalPages}</Pagination.Item>}
        </>
      );
    }
    return Array.from({ length: totalPages }, (_, i) => (
      <Pagination.Item key={i + 1} active={i + 1 === currentPage} onClick={() => handlePageChange(i + 1)}>{i + 1}</Pagination.Item>
    ));
  };

  /* ─── Sorting ─── */
  const handleSort = (key) =>
    setSortConfig((prev) => ({ key, direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc' }));

  const sortedData = (() => {
    if (!sortConfig.key) return reportData;
    return [...reportData].sort((a, b) => {
      const aVal = a[sortConfig.key] ?? (typeof a[sortConfig.key] === 'string' ? '' : 0);
      const bVal = b[sortConfig.key] ?? (typeof b[sortConfig.key] === 'string' ? '' : 0);
      if (typeof aVal === 'string')
        return sortConfig.direction === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
    });
  })();

  const SortIcon = ({ colKey }) =>
    sortConfig.key !== colKey
      ? <span style={{ opacity: 0.3, fontSize: 10, marginLeft: 3 }}>⇅</span>
      : <span style={{ fontSize: 10, marginLeft: 3, color: '#0d6efd' }}>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>;

  /* ─── Totals ─── */
  const totals = reportData?.reduce(
    (acc, item) => {
      acc.totalAllocatedUsers += item.totalAllocatedUsers || 0;
      acc.totalUsedUsers += item.totalUsedUsers || 0;
      acc.totalTaxable += item.totalTaxable || 0;
      acc.totalTaxAmount += item.totalTaxAmount || 0;
      acc.totalDiscount += item.totalDiscount || 0;
      acc.totalAvgDiscount += item.averageDiscount || 0;
      return acc;
    },
    { totalAllocatedUsers: 0, totalUsedUsers: 0, totalTaxAmount: 0, totalTaxable: 0, totalDiscount: 0, totalAvgDiscount: 0 }
  );

  /* ─── Derived flags ─── */
  const isMultiSelected = selectedOutlets.length > 1;
  const isLong = isLongRange(startDate, endDate);
  const hasAnyData = reportData.length > 0;

  /* ─── Style helpers ─── */
  const tdStyle = { verticalAlign: 'middle', padding: '8px 12px', borderColor: '#f1f5f9' };
  const thStyle = {
    verticalAlign: 'middle', padding: '10px 12px',
    fontSize: 11, fontWeight: 700, letterSpacing: '0.6px',
    textTransform: 'uppercase', color: '#64748b',
    borderBottom: '2px solid #e2e8f0', backgroundColor: '#f8fafc',
    cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap',
  };

  const ThSortable = ({ colKey, align = 'left', children }) => (
    <th style={{ ...thStyle, textAlign: align }} onClick={() => handleSort(colKey)}>
      {children}<SortIcon colKey={colKey} />
    </th>
  );

  /* ════════════════════════════════ JSX ════════════════════════════════ */
  return (
    <React.Fragment>
      <style>{DM_CSS}</style>
      <style>{`
        .jr-filter-box{background:#f8fafc;border:1px solid #e9ecef;border-radius:12px;padding:18px;}
        .jr-table tbody tr:hover{background-color:#f0f7ff !important;transition:background .1s;}
        .jr-stat-chip{display:inline-flex;align-items:center;gap:6px;background:#e7f0ff;border-radius:8px;padding:4px 12px;font-size:12px;color:#0d6efd;font-weight:600;}
        .jr-multi-notice{margin:12px 0;padding:14px 18px;border-radius:8px;background:#fffbeb;border:1.5px solid #fde68a;display:flex;align-items:flex-start;gap:12px;}
        .jr-multi-notice-text{font-size:13px;color:#92400e;line-height:1.6;}
        .jr-multi-notice-text strong{color:#78350f;}
      `}</style>

      <Header onSkin={setSkin} />

      <div className={`main main-app p-3 p-lg-4 ${skin === 'dark' ? 'bg-dark text-light' : 'bg-light'}`}>
        <div className="card border-0" style={{ borderRadius: 8, boxShadow: '0 1px 3px rgba(0,0,0,.06),0 4px 16px rgba(0,0,0,.04)', border: '1px solid #e2e8f0' }}>

          {/* ── Page Header ── */}
          <div className="card-header bg-white border-0 py-3 px-4 d-flex justify-content-between align-items-center" style={{ borderRadius: '16px 16px 0 0' }}>
            <div className="d-flex align-items-center gap-3">
              <div style={{ width: 42, height: 42, borderRadius: 10, background: 'linear-gradient(135deg,#0d6efd,#6ea8fe)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <i className="bi bi-graph-up text-white fs-5"></i>
              </div>
              <div>
                <h5 className="mb-0 fw-bold" style={{ color: '#1e293b', letterSpacing: '-0.3px' }}>JOURNEY COUPON REVENUE REPORT</h5>
                <div style={{ fontSize: 12, color: '#94a3b8' }}>Track journey coupon usage and revenue across outlets</div>
              </div>
            </div>
            <div className="d-flex align-items-center gap-2">
              <button
                id="jr-dm-fab"
                className={`jr-dm-fab${pendingCount > 0 ? ' has-active' : ''}`}
                onClick={() => setDmOpen((o) => !o)}
                title="Download Manager"
              >
                <CloudDownload size={15} />
                Downloads
                {pendingCount > 0 && <span className="jr-dm-badge jr-pulse">{pendingCount}</span>}
                {pendingCount === 0 && completedCount > 0 && (
                  <span className="jr-dm-badge" style={{ background: '#059669' }}>{completedCount}</span>
                )}
              </button>
              <Button variant="outline-secondary" size="sm" onClick={() => navigate('/reports/all-reports')}
                className="d-flex align-items-center fw-semibold" style={{ borderRadius: 4, height: 36 }}>
                <ArrowLeft className="me-1" size={14} /> Back to Reports
              </Button>
            </div>
          </div>
          <div style={{ height: 1, background: '#e2e8f0' }} />

          <div className="card-body p-4">
            <Form onSubmit={handleSubmit}>
              {/* ── Filters ── */}
              <div className="jr-filter-box mb-3">
                <Row className="g-3">
                  <Col md={4}>
                    <OutletSelect
                      value={selectedOutlets}
                      onChange={(opts) => setSelectedOutlets(safeArray(opts))}
                      isMulti={true}
                      required={false}
                    />
                    <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>
                      Optional — leave blank to fetch all outlets
                    </div>
                  </Col>
                  <Col md={2}>
                    <Form.Label className="fw-semibold" style={{ fontSize: 14, color: '#475569' }}>
                      <i className="bi bi-calendar3 me-1"></i>Start Date
                    </Form.Label>
                    <DatePicker
                      selected={startDate}
                      onChange={(date) => setStartDate(date)}
                      dateFormat="dd-MM-yyyy"
                      className="form-control"
                      maxDate={new Date()}
                    />
                  </Col>
                  <Col md={2}>
                    <Form.Label className="fw-semibold" style={{ fontSize: 14, color: '#475569' }}>
                      <i className="bi bi-calendar3 me-1"></i>End Date
                    </Form.Label>
                    <DatePicker
                      selected={endDate}
                      onChange={(date) => {
                        if (startDate && new Date(date) < new Date(startDate)) {
                          setError('End date cannot be earlier than start date');
                        } else {
                          setError('');
                          setEndDate(date);
                        }
                      }}
                      dateFormat="dd-MM-yyyy"
                      minDate={startDate}
                      maxDate={new Date()}
                      className="form-control"
                    />
                    {error && <div className="text-danger" style={{ fontSize: 11, marginTop: 4 }}>{error}</div>}
                  </Col>
                </Row>
              </div>

              {/* ── Actions bar ── */}
              <div className="jr-filter-box mb-4">
                <Row className="g-3 align-items-end">
                  <Col md={3}>
                    <Button variant="primary" size="sm" type="submit" className="w-100 fw-semibold"
                      disabled={!startDate || !endDate || btnLoading} style={{ borderRadius: 4, height: 35 }}>
                      {btnLoading
                        ? <><Spinner size="sm" animation="border" className="me-2" />Loading…</>
                        : (isMultiSelected && isLong) || isLong
                          ? <><FileEarmarkSpreadsheet className="me-2" size={14} />Schedule Download</>
                          : <><i className="bi bi-bar-chart-line me-2"></i>Generate Report</>}
                    </Button>
                    {isLong && (
                      <span style={{ fontSize: 11, color: '#d97706', fontWeight: 500, display: 'flex', alignItems: 'center', gap: 5, marginTop: 4 }}>
                        <ExclamationTriangle size={11} />
                        Date range &gt;90 days — will queue as Excel download
                      </span>
                    )}
                  </Col>

                  {/* Inline Excel download — only when not in schedule mode and data exists */}
                  {!isLong && hasAnyData && (
                    <Col md={2}>
                      <Button variant="outline-success" className="w-100 fw-semibold"
                        onClick={handleDownloadExcel} disabled={downloadBtnLoading} style={{ height: 35 }}>
                        <FileEarmarkSpreadsheet className="me-2" />
                        {downloadBtnLoading ? <Spinner size="sm" animation="border" /> : 'Download Excel'}
                      </Button>
                    </Col>
                  )}

                  {/* Summary chips */}
                  {!isScheduledMode && hasAnyData && (
                    <Col xs={12} className="d-flex gap-2 flex-wrap pt-1">
                      <span className="jr-stat-chip"><i className="bi bi-ticket-perforated"></i>{totalRecords} Records</span>
                      <span className="jr-stat-chip"><i className="bi bi-people"></i>{totals.totalAllocatedUsers} Allocated</span>
                      <span className="jr-stat-chip" style={{ background: '#dcfce7', color: '#16a34a' }}>
                        <i className="bi bi-check-circle"></i>{totals.totalUsedUsers} Used
                      </span>
                      <span className="jr-stat-chip" style={{ background: '#fee2e2', color: '#dc3545' }}>
                        <i className="bi bi-tag"></i>{fmtCurrency(totals.totalDiscount)} Discount
                      </span>
                      <span className="jr-stat-chip" style={{ background: '#dcfce7', color: '#16a34a' }}>
                        <i className="bi bi-currency-rupee"></i>{fmtCurrency(totals.totalTaxable)} Generated Revenue
                      </span>
                      <span className="jr-stat-chip" style={{ background: '#f1f5f9', color: '#475569' }}>
                        <i className="bi bi-calculator"></i>{fmtCurrency(totals.totalAvgDiscount)} Avg. Discount
                      </span>
                    </Col>
                  )}
                </Row>
              </div>
            </Form>

            {/* ── Multi-outlet / scheduled notice ── */}
            {clicked && isScheduledMode && (
              <div className="jr-multi-notice">
                <span style={{ fontSize: 18, flexShrink: 0 }}>📥</span>
                <div className="jr-multi-notice-text">
                  <strong>Download queued.</strong>{' '}
                  Your report is being generated in the background. Click the{' '}
                  <strong>Downloads</strong> button in the header to track progress and download once ready.
                </div>
              </div>
            )}

            {/* ── Empty / loading / data states ── */}
            {!clicked ? (
              <div className="d-flex flex-column justify-content-center align-items-center py-5 my-3 rounded-3"
                style={{ background: '#f8fafc', border: '2px dashed #e2e8f0', minHeight: 240 }}>
                <div style={{ width: 64, height: 64, borderRadius: 16, background: '#e7f0ff', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16 }}>
                  <i className="bi bi-graph-up text-primary fs-3"></i>
                </div>
                <h6 className="text-muted mb-1">No data to display</h6>
                <p className="text-muted mb-0" style={{ fontSize: 13 }}>Select filters above and click <strong>Generate Report</strong></p>
              </div>

            ) : loading ? (
              <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />
                <p className="mt-2 mb-0 text-muted" style={{ fontSize: 13 }}>Loading report data…</p>
              </div>

            ) : isScheduledMode ? (
              <div className="d-flex flex-column justify-content-center align-items-center py-5 my-3 rounded-3"
                style={{ background: '#f8fafc', border: '2px dashed #e2e8f0', minHeight: 200 }}>
                <div style={{ width: 64, height: 64, borderRadius: 16, background: '#e7f0ff', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16 }}>
                  <CloudDownload size={28} className="text-primary" />
                </div>
                <h6 className="text-muted mb-1">Report queued for download</h6>
                <p className="text-muted mb-0" style={{ fontSize: 13 }}>
                  Check the <strong>Downloads</strong> panel for your Excel file.
                </p>
              </div>

            ) : !hasAnyData ? (
              <div className="text-center py-5" style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 12 }}>
                <i className="bi bi-inbox fs-3 text-muted d-block mb-2"></i>
                <span className="text-muted" style={{ fontSize: 13 }}>No data found for the selected criteria</span>
              </div>

            ) : (
              /* ══════════ DATA TABLE ══════════ */
              <>
                {/* Pagination top */}
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <div className="d-flex align-items-center gap-2">
                    <span className="text-muted" style={{ fontSize: 13 }}>Show</span>
                    <Form.Select
                      style={{ width: 'auto', fontSize: 13 }}
                      value={recordsPerPage}
                      onChange={(e) => setRecordsPerPage(Number(e.target.value))}
                    >
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                      <option value={200}>200</option>
                    </Form.Select>
                    <span className="text-muted" style={{ fontSize: 13 }}>entries</span>
                  </div>
                  <Pagination className="mb-0" size="sm">
                    <Pagination.Prev disabled={currentPage === 1} onClick={() => handlePageChange(currentPage - 1)} />
                    {renderPaginationItems()}
                    <Pagination.Next disabled={currentPage === totalPages} onClick={() => handlePageChange(currentPage + 1)} />
                  </Pagination>
                </div>

                <div style={{ borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                    <div className='table-responsive'>
                  <Table className="mb-0 jr-table" style={{ fontSize: 13 }}>
                    <thead>
                      <tr>
                        <th style={{ ...thStyle, width: 44 }}>Sr No</th>
                        <ThSortable colKey="autoCouponAllocation.title">Journey Coupon</ThSortable>
                        <ThSortable colKey="autoCouponAllocation.nthOrderNumber" align="center">Nth Order No</ThSortable>
                        <ThSortable colKey="autoCouponAllocation.minOrderAmount" align="right">Min & Max Order Amt</ThSortable>
                        {/* <ThSortable colKey="autoCouponAllocation.maxOrderAmount" align="right">Max Order Amt</ThSortable> */}
                        <ThSortable colKey="totalAllocatedUsers" align="center">Tot Allocated Users</ThSortable>
                        <ThSortable colKey="totalUsedUsers" align="center">Tot Used Users</ThSortable>
                        <ThSortable colKey="totalDiscount" align="right">Tot Discount</ThSortable>
                        <ThSortable colKey="totalTaxable" align="right">Tot Revenue</ThSortable>
                        <ThSortable colKey="averageDiscount" align="right">Tot Avg. Disc</ThSortable>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedData.map((item, idx) => {
                        const aca = item.autoCouponAllocation || {};
                        // _id can be an object {autoCouponAllocationId, outlet} or a plain string
                        const rowKey = typeof item._id === 'object'
                          ? `${item._id?.autoCouponAllocationId}-${item._id?.outlet}`
                          : item._id || idx;
                        return (
                          <tr key={rowKey}>
                            <td style={{ ...tdStyle, color: '#94a3b8', fontSize: 12 }}>
                              {((currentPage - 1) * recordsPerPage) + idx + 1}
                            </td>
                            {/* Journey Coupon */}
                            <td style={{ ...tdStyle, fontWeight: 600, color: '#1e293b' }}>
                              {aca.title || '—'}
                            </td>
                            {/* Nth Order Number */}
                            <td style={{ ...tdStyle, textAlign: 'center' }}>
                              {aca.nthOrderNumber != null ? (
                                <span style={{
                                  display: 'inline-block', padding: '2px 10px', borderRadius: 20,
                                  fontSize: 11, fontWeight: 700,
                                   color: '#0369a1',
                                }}>
                                  {aca.nthOrderNumber}
                                </span>
                              ) : (
                                <span style={{ color: '#94a3b8', fontSize: 12 }}>—</span>
                              )}
                            </td>
                            {/* Min Order Amount */}
                            <td style={{ ...tdStyle, textAlign: 'right', color: '#475569' }}>
                              <span className='text-danger'>{aca.minOrderAmount != null ? fmtCurrency(aca.minOrderAmount) : <span style={{ color: '#94a3b8' }}>—</span>}</span> / <span className='text-success fw-semibold'>{aca.maxOrderAmount != null ? fmtCurrency(aca.maxOrderAmount) : <span style={{ color: '#94a3b8' }}>—</span>}</span>
                            </td>
                            {/* Max Order Amount */}
                            {/* <td style={{ ...tdStyle, textAlign: 'right', color: '#475569' }}>
                              {aca.maxOrderAmount != null ? fmtCurrency(aca.maxOrderAmount) : <span style={{ color: '#94a3b8' }}>—</span>}
                            </td> */}
                            {/* Total Allocated Users */}
                            <td style={{ ...tdStyle, textAlign: 'center', fontWeight: 600 }}>
                              {item.totalAllocatedUsers ?? 0}
                            </td>
                            {/* Total Used Users */}
                            <td style={{ ...tdStyle, textAlign: 'center', fontWeight: 700, color: '#16a34a' }}>
                              {item.totalUsedUsers ?? 0}
                            </td>
                            {/* Total Discount */}
                            <td style={{ ...tdStyle, textAlign: 'right', color: '#dc3545', fontWeight: 600 }}>
                              {fmtCurrency(item.totalDiscount)}
                            </td>
                            {/* Generated Revenue = Grand Total */}
                            <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 700, color: '#1e293b' }}>
                              {fmtCurrency(item.totalTaxable)}
                            </td>
                            {/* Total Average Discount */}
                            <td style={{ ...tdStyle, textAlign: 'right', color: '#475569' }}>
                              {fmtCurrency(item.averageDiscount)}
                            </td>
                          </tr>
                        );
                      })}
                      {/* Totals row — colSpan covers #, Journey Coupon, Nth, Min, Max = 5 cols */}
                      <tr style={{ backgroundColor: '#1e3a5f' }}>
                        <td colSpan={4} style={{ ...tdStyle, textAlign: 'right', color: 'white', backgroundColor: '#1e3a5f', fontWeight: 700, fontSize: 11, letterSpacing: '1px', textTransform: 'uppercase' }}>Total</td>
                        <td style={{ ...tdStyle, textAlign: 'center', color: 'white', fontWeight: 700, backgroundColor: '#1e3a5f' }}>{totals.totalAllocatedUsers}</td>
                        <td style={{ ...tdStyle, textAlign: 'center', color: '#86efac', fontWeight: 700, backgroundColor: '#1e3a5f' }}>{totals.totalUsedUsers}</td>
                        <td style={{ ...tdStyle, textAlign: 'right', color: '#fca5a5', fontWeight: 600, backgroundColor: '#1e3a5f' }}>{fmtCurrency(totals.totalDiscount)}</td>
                        <td style={{ ...tdStyle, textAlign: 'right', color: 'white', fontWeight: 700, backgroundColor: '#1e3a5f' }}>{fmtCurrency(totals.totalTaxable)}</td>
                        <td style={{ ...tdStyle, textAlign: 'right', color: '#cbd5e1', backgroundColor: '#1e3a5f' }}>{fmtCurrency(totals.totalAvgDiscount)}</td>
                      </tr>
                    </tbody>
                  </Table></div>
                </div>

                {/* Pagination bottom */}
                <div className="d-flex justify-content-between align-items-center mt-3">
                  <div className="text-muted" style={{ fontSize: 13 }}>
                    Showing {((currentPage - 1) * recordsPerPage) + 1} to {Math.min(currentPage * recordsPerPage, totalRecords)} of {totalRecords} entries
                  </div>
                  <Pagination className="mb-0" size="sm">
                    <Pagination.Prev disabled={currentPage === 1} onClick={() => handlePageChange(currentPage - 1)} />
                    {renderPaginationItems()}
                    <Pagination.Next disabled={currentPage === totalPages} onClick={() => handlePageChange(currentPage + 1)} />
                  </Pagination>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* ══════════════════════════════════════════
          CUMULATIVE CHOICE MODAL
          (appears when multiple outlets + >90 days)
      ══════════════════════════════════════════ */}
      <Modal show={showCumulativeModal} onHide={() => setShowCumulativeModal(false)} centered>
        <Modal.Header closeButton style={{ background: 'linear-gradient(135deg,#0f172a,#1e3a8a)', color: '#fff', border: 'none' }}>
          <Modal.Title style={{ fontSize: 15, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
            <FileEarmarkSpreadsheet size={17} /> Choose Download Format
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          <p style={{ fontSize: 13, color: '#475569', marginBottom: 20 }}>
            You've selected <strong>{selectedOutlets.length} outlets</strong> with a date range greater than 90 days.
            How would you like the report to be generated?
          </p>
          <div className="d-flex flex-column gap-3">
            <button
              onClick={() => { setShowCumulativeModal(false); triggerScheduledDownload(true); }}
              style={{
                padding: '16px 20px', borderRadius: 10, border: '2px solid #bfdbfe',
                background: '#eff6ff', cursor: 'pointer', textAlign: 'left', transition: 'all .15s',
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = '#2563eb'; e.currentTarget.style.background = '#dbeafe'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = '#bfdbfe'; e.currentTarget.style.background = '#eff6ff'; }}
            >
              <div style={{ fontWeight: 700, color: '#1e40af', fontSize: 14, marginBottom: 4 }}>
                📊 Cumulative Report
              </div>
              <div style={{ fontSize: 12, color: '#3b82f6' }}>
                All outlets merged into a single summary — one Excel sheet with aggregated totals.
              </div>
            </button>
            <button
              onClick={() => { setShowCumulativeModal(false); triggerScheduledDownload(false); }}
              style={{
                padding: '16px 20px', borderRadius: 10, border: '2px solid #bbf7d0',
                background: '#f0fdf4', cursor: 'pointer', textAlign: 'left', transition: 'all .15s',
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = '#059669'; e.currentTarget.style.background = '#dcfce7'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = '#bbf7d0'; e.currentTarget.style.background = '#f0fdf4'; }}
            >
              <div style={{ fontWeight: 700, color: '#065f46', fontSize: 14, marginBottom: 4 }}>
                🏪 Outlet-wise Report
              </div>
              <div style={{ fontSize: 12, color: '#059669' }}>
                Each outlet gets its own breakdown — separate data per outlet in the Excel file.
              </div>
            </button>
          </div>
        </Modal.Body>
        <Modal.Footer style={{ border: 'none', paddingTop: 0 }}>
          <Button variant="outline-secondary" size="sm" onClick={() => setShowCumulativeModal(false)}>
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ══════════════════════════════════════════
          FLOATING DOWNLOAD MANAGER PANEL
      ══════════════════════════════════════════ */}
      {dmOpen && (
        <div className="jr-dm-panel" ref={dmPanelRef}>
          <div className="jr-dm-header">
            <div className="jr-dm-header-left">
              <div className="jr-dm-header-title">
                <CloudDownload size={17} /> Download Manager
              </div>
              <div className="jr-dm-header-sub">Scheduled Excel export jobs</div>

              {/* DM date filter */}
              <div style={{ display: 'flex', gap: 10, marginTop: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                {[{ label: 'From', key: 'start', val: dmStartDate }, { label: 'To', key: 'end', val: dmEndDate }].map(({ label, key, val }) => (
                  <div key={key} style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <span style={{ fontSize: 9.5, fontWeight: 600, color: 'rgba(255,255,255,.55)', textTransform: 'uppercase', letterSpacing: '.6px' }}>{label}</span>
                    <DatePicker
                      selected={val}
                      onChange={(date) => handleDmDateChange(key, date)}
                      dateFormat="dd-MM-yyyy"
                      maxDate={key === 'end' ? (() => {
                        if (!dmStartDate) return new Date();
                        const max = new Date(dmStartDate); max.setDate(max.getDate() + 30);
                        const today = new Date(); return max > today ? today : max;
                      })() : new Date()}
                      minDate={key === 'end' ? dmStartDate : undefined}
                      customInput={
                        <input style={{
                          height: 30, padding: '0 10px', borderRadius: 6,
                          border: '1px solid rgba(255,255,255,.2)',
                          background: 'rgba(255,255,255,.1)', color: '#fff',
                          fontSize: 12, fontWeight: 500, cursor: 'pointer', outline: 'none', width: 112,
                        }} />
                      }
                    />
                  </div>
                ))}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  <span style={{ fontSize: 9.5, color: 'transparent' }}>–</span>
                  <button
                    style={{ height: 30, padding: '0 12px', borderRadius: 6, border: '1px solid rgba(255,255,255,.25)', background: 'rgba(255,255,255,.15)', color: '#fff', fontSize: 11.5, fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 5 }}
                    onClick={() => fetchDownloadStatus(false)}
                    disabled={jobsLoading}
                  >
                    Apply
                  </button>
                </div>
                <span style={{ fontSize: 10, color: 'rgba(255,255,255,.45)', alignSelf: 'flex-end', marginBottom: 3 }}>Max 30-day range</span>
              </div>

              <div className="jr-dm-stats">
                {[
                  { val: downloadJobs.length, lbl: 'Total' },
                  { val: pendingCount, lbl: 'In Progress' },
                  { val: completedCount, lbl: 'Ready' },
                  { val: downloadJobs.filter(j => getJobCls(j.status) === 'failed').length, lbl: 'Failed' },
                ].map(({ val, lbl }) => (
                  <div key={lbl} className="jr-dm-stat">
                    <div className="jr-dm-stat-val">{val}</div>
                    <div className="jr-dm-stat-lbl">{lbl}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="jr-dm-header-actions">
              {pollingActive && (
                <span style={{ fontSize: 11, color: 'rgba(255,255,255,.65)', display: 'flex', alignItems: 'center', gap: 4, marginRight: 4 }}>
                  <span className="jr-spin"><ArrowClockwise size={11} /></span> Live
                </span>
              )}
              <button className="jr-dm-icon-btn"
                onClick={() => { setJobsLoading(true); fetchDownloadStatus().finally(() => setJobsLoading(false)); }}
                disabled={jobsLoading}>
                {jobsLoading ? <Spinner size="sm" animation="border" style={{ width: 12, height: 12 }} /> : <ArrowClockwise size={13} />}
              </button>
              <button className="jr-dm-icon-btn" onClick={() => setDmOpen(false)} title="Close"><X size={14} /></button>
            </div>
          </div>

          <div className="jr-dm-body">
            {downloadJobs.length === 0 ? (
              <div className="jr-dm-empty">
                <CloudDownload size={40} style={{ opacity: 0.2 }} />
                <div style={{ fontSize: 14, fontWeight: 600, color: '#374151' }}>No download jobs yet</div>
                <div style={{ fontSize: 12.5, color: '#9ca3af', maxWidth: 280 }}>
                  Select a long date range or multiple outlets and click <strong>Schedule Download</strong>.
                </div>
              </div>
            ) : (
              downloadJobs.map((job, idx) => {
                const cls = getJobCls(job.status);
                const qp = job.queryParams || {};
                const outlets = job.requestBody?.outletIds || [];
                return (
                  <div key={job._id || idx} className="jr-dm-job">
                    <div className={`jr-dm-job-icon ${cls}`}>{getJobIcon(job.status)}</div>
                    <div className="jr-dm-job-info">
                      <div className="jr-dm-job-name">
                        Journey Coupon Revenue — {qp.startDate || '?'} → {qp.endDate || '?'}
                        {qp.isCumilative === 'true' && (
                          <span style={{ marginLeft: 6, fontSize: 10, background: '#eff6ff', color: '#2563eb', padding: '1px 6px', borderRadius: 10, border: '1px solid #bfdbfe', fontWeight: 700 }}>Cumulative</span>
                        )}
                        {qp.isCumilative === 'false' && (
                          <span style={{ marginLeft: 6, fontSize: 10, background: '#f0fdf4', color: '#059669', padding: '1px 6px', borderRadius: 10, border: '1px solid #bbf7d0', fontWeight: 700 }}>Outlet-wise</span>
                        )}
                      </div>
                      <div className="jr-dm-job-meta">
                        <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                          <Clock size={10} /> {fmtViewDate(job.createdAt)} {fmtViewTime(job.createdAt)}
                        </span>
                        <span>·</span>
                        <span>{outlets?.length > 0 ? outlets?.length : 'All' } outlet{outlets.length !== 1 ? 's' : ''}</span>
                        {job.errorMessage && <span style={{ color: 'var(--jr-danger)' }}>· {job.errorMessage}</span>}
                      </div>
                      <div className="jr-pb-wrap"><div className={`jr-pb ${cls}`} /></div>
                    </div>
                    <span className={`jr-status-pill ${cls}`}>{getJobLabel(job.status)}</span>
                    {cls === 'completed' && job.storagePath && (
                      <button className="jr-dl-btn" onClick={() => downloadFile(job)}>
                        <BoxArrowDown size={12} /> Download
                      </button>
                    )}
                    {cls === 'completed' && (
                      <button className="jr-regen-btn" title="Regenerate" disabled={regeneratingJobId === job._id}
                        onClick={() => handleRegenerateReport(job._id)}>
                        {regeneratingJobId === job._id
                          ? <Spinner animation="border" size="sm" style={{ width: 12, height: 12 }} />
                          : <ArrowClockwise size={14} />}
                      </button>
                    )}
                    <button className="jr-del-btn" title="Cancel" disabled={deletingJobId === job._id}
                      onClick={() => handleDeleteReportRequest(job._id)}>
                      {deletingJobId === job._id
                        ? <Spinner animation="border" size="sm" style={{ width: 12, height: 12 }} />
                        : <XCircle size={14} />}
                    </button>
                  </div>
                );
              })
            )}
          </div>

          <div className="jr-dm-footer">
            <span className="jr-dm-footer-info">
              {pollingActive && (
                <><span className="jr-spin" style={{ display: 'inline-flex' }}><ArrowClockwise size={12} /></span> Auto-refreshing every 5s</>
              )}
            </span>
            <button
              style={{ height: 30, padding: '0 14px', borderRadius: 6, border: '1.5px solid #e2e6ea', background: '#fff', fontSize: 12, fontWeight: 500, color: 'var(--jr-muted)', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 5, opacity: pollingActive ? 0.5 : 1 }}
              onClick={startPolling} disabled={pollingActive}>
              <ArrowClockwise size={11} /> Refresh
            </button>
          </div>
        </div>
      )}
    </React.Fragment>
  );
};

export default JourneyRevenueReport;